import React from "react";
import { FieldModel, Group } from "./types";
import BorderColorIcon from "@material-ui/icons/BorderColor";
import EditIcon from "@material-ui/icons/Edit";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { typeOfDocumentsLabels } from "../utils/typeOfDocuments";
import { recipientsService } from "../../../services/recipientsService";
import { groupColorService } from "./groupColorService";

const fieldTypes = [
	{
		label: "Short Text",
		value: "shortText",
		icon: <TextFieldsIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.shortText,
	},
	{
		label: "Long Text",
		value: "longText",
		icon: <TextFieldsIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.text,
	},
	{
		label: "Checkbox",
		value: "checkbox",
		icon: <CheckBoxIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.checkbox,
	},
	{
		label: "Radio Button",
		value: "radio",
		icon: <RadioButtonCheckedIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.radio,
	},
	{
		label: "Number",
		value: "number",
		icon: <TextFieldsIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.number,
	},
	{
		label: "Date",
		value: "date",
		icon: <TextFieldsIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.date,
	},
	{
		label: "File Upload",
		value: "file",
		icon: <AttachFileIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.file,
	},
	{
		label: "Signature",
		value: "signature",
		icon: <BorderColorIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.signature,
	},
	{
		label: "Initials",
		value: "initials",
		icon: <EditIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.initials,
	},
];

export function createFieldsPluginRecipients(
	fields: FieldModel[],
	onMove: (id: string, newBounds: Partial<FieldModel['bounds']>) => void,
	onSelect: (id: string) => void,
	selectedId: string | null,
	onContextMenu?: (event: React.MouseEvent, fieldIndex: number, fieldId?: string) => void,
	fieldAssignments?: Record<string, string>,
	onFieldSelect?: (fieldId: string) => void,
	recipients?: Array<{ id: string; name: string; email: string }>,
	groups?: Group[]
) {
	return {
		renderPageLayer(props: any) {
			const { pageIndex, width, height } = props;

			// Calculate pixels per inch based on the actual page width (8.5 inches)
			const ppi = width / 8.5; // px por pulgada (escala actual)

			const pageFields = fields.filter((f) => f.bounds.page === pageIndex + 1);

			return (
				<div
					style={{
						position: "relative",
						width: width,
						height: height,
					}}
				>
					{pageFields.map((f) => {
						const { x, y, width: fieldWidth, height: fieldHeight } = f.bounds;

						// Convert inches to pixels - same calculation as original plugin
						const left = x * ppi;
						const top = y * ppi; // Use same Y calculation as original
						const w = fieldWidth * ppi;
						const h = fieldHeight * ppi;

            const iconField = fieldTypes.find(field => field.value === f.type)?.icon;
						const isAssigned = fieldAssignments && fieldAssignments[f.id];
						const isAssignableField = true; // Allow all fields to be assignable

						// Get assigned recipient info for tooltip and color
						const assignedRecipient = isAssigned && recipients 
							? recipients.find(r => r.id === fieldAssignments[f.id])
							: null;
						
						// Get the color for the assigned recipient
						const recipientColor = assignedRecipient 
							? recipientsService.getRecipientColor(assignedRecipient.id)
							: null;

						// Get group color from persisted fieldGroupColor or fallback to group lookup
						let groupColorScheme = null;
						if (f.fieldGroupColor) {
							// Use persisted color directly
							groupColorScheme = groupColorService.getColorByPrimaryColor(f.fieldGroupColor);
						} else if (f.fieldGroup && groups) {
							// Fallback: lookup from groups array
							const assignedGroup = groups.find(g => g.name === f.fieldGroup);
							if (assignedGroup) {
								groupColorScheme = groupColorService.getGroupColor(assignedGroup);
							}
						}

						return (
							<div
								key={f.id}
								style={{
									position: "absolute",
									left: left,
									top: top,
									width: w,
									height: h,
									outline: (() => {
										// Priority: Selected field -> Recipient assignment -> Group assignment -> Default
										if (selectedId === f.id) {
											return "3px solid #2196F3"; // Blue for selected field
										} else if (isAssigned && recipientColor) {
											return `3px solid ${recipientColor}`; // Recipient color for assigned fields
										} else if (groupColorScheme) {
											return `3px solid ${groupColorScheme.border}`; // Group color for grouped fields
										}
										return "3px solid #ff9800"; // Orange for unassigned fields
									})(),
									boxSizing: "border-box",
									background: (() => {
										// Priority: Selected field -> Recipient assignment -> Group assignment -> Default
										if (selectedId === f.id) {
											return "rgba(33, 150, 243, 0.3)"; // Light blue for selected field
										} else if (isAssigned && recipientColor) {
											return `${recipientColor}80`; // Recipient color with transparency
										} else if (groupColorScheme) {
											return groupColorScheme.secondary; // Group secondary color
										}
										return "rgba(255, 152, 0, 0.5)"; // Orange background for unassigned fields
									})(),
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									cursor: "pointer", // All fields are clickable now
									zIndex: 1000,
									color: "rgba(0, 106, 198, 1)",
									pointerEvents: "auto",
								}}
								onClick={(e) => {
									e.stopPropagation();
									console.log("Field clicked:", f.id, "onFieldSelect:", !!onFieldSelect, "isAssignableField:", isAssignableField);
									if (onFieldSelect && isAssignableField) {
										console.log("Calling onFieldSelect with:", f.id);
										onFieldSelect(f.id);
									} else if (isAssignableField) {
										console.log("Calling onSelect with:", f.id);
										onSelect(f.id);
									}
								}}
								onContextMenu={(e) => {
									if (onContextMenu) {
										e.preventDefault();
										e.stopPropagation();
										const fieldIndex = fields.findIndex(field => field.id === f.id);
										onContextMenu(e, fieldIndex, f.id);
									}
								}}
								title={
									isAssigned && assignedRecipient
										? `${f.title} - Assigned to: ${assignedRecipient.name}`
										: `${f.title} - Click to assign recipient`
								}
							>
								<span style={{ 
									display: "flex", 
									alignItems: "center", 
									justifyContent: "center", 
									fontSize: "8px",
									flexDirection: "column",
									gap: "2px"
								}}>
									<span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
										{iconField} {f.title}
									</span>
									{isAssigned && (
										<span style={{ 
											fontSize: "6px", 
											color: "white",
											backgroundColor: "rgba(0,0,0,0.5)",
											padding: "1px 4px",
											borderRadius: "2px"
										}}>
											✓ Assigned
										</span>
									)}
									{isAssignableField && !isAssigned && (
										<span style={{ 
											fontSize: "6px", 
											color: "white",
											backgroundColor: "rgba(0,0,0,0.5)",
											padding: "1px 4px",
											borderRadius: "2px"
										}}>
											Click to assign
										</span>
									)}
								</span>
							</div>
						);
					})}
				</div>
			);
		},
	};
}
